import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { canAccessFeature } from '../../hooks/usePermissions';
import UnifiedLoader from '../common/UnifiedLoader';
import Loader from '../common/Loader';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredFeature?: keyof ReturnType<typeof canAccessFeature>;
  fallbackPath?: string;
  requireAdmin?: boolean;
}

export default function ProtectedRoute({ 
  children, 
  requiredFeature, 
  fallbackPath = '/neo-intelligence', 
  requireAdmin = false 
}: ProtectedRouteProps) {
  const { user, loading, token } = useAuth();

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-20 h-20 mx-auto" />
          {/* <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Loading...</p> */}
        </div>
      </div>
    );
  }

  // Check localStorage directly as a fallback (in case state hasn't updated yet)
  const hasTokenInStorage = localStorage.getItem('token') !== null;
  const hasUserInStorage = localStorage.getItem('user') !== null;
  
  // Check if we're on shopify-app page with auth_token (from billing return)
  // This means we're in process of auto-login and should wait
  const isShopifyAppPage = typeof window !== 'undefined' && window.location.pathname === '/shopify-app';
  const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const hasAuthTokenInUrl = urlParams?.get('auth_token') !== null;

  // If we have auth_token in URL, we're setting up auto-login - wait a bit longer
  if (isShopifyAppPage && hasAuthTokenInUrl && hasTokenInStorage && !user && loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <Loader className="w-20 h-20 mx-auto" />
                    {/* <p className="mt-4 text-gray-600">Completing authentication...</p> */}
                </div>
            </div>
        );
    }

  // Show loading while checking authentication (but only briefly)
  if (loading && !hasTokenInStorage && !hasUserInStorage) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <Loader className="w-20 h-20 mx-auto" />
                    {/* <p className="mt-4 text-gray-600">Loading...</p> */}
                </div>
            </div>
        );
    }

  // Only redirect if we have no user AND no token (definite no-auth state)
  // BUT: If we have a token in storage, give it a moment to load user data
  // This is important for auto-login from billing return
  const hasAuth = (user || token || hasUserInStorage || hasAuthTokenInUrl);
    
    // If we have token but no user yet, and we're on shopify-app page, wait a bit
    if (isShopifyAppPage && hasTokenInStorage && !user && !loading) {
        // Give it a moment - user data might still be loading
        // Don't redirect immediately
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <Loader className="w-20 h-20 mx-auto" />
                    {/* <p className="mt-4 text-gray-600">Loading user data...</p> */}
                </div>
            </div>
        );
    }

  if (!hasAuth) {
        return <Navigate to={`/signin?redirect=${encodeURIComponent(window.location.pathname)}`} replace />;
    }

  // Check feature-based permissions
  if (requiredFeature && !canAccessFeature(requiredFeature)) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="max-w-md w-full mx-4 text-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
            <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Access Denied
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              You don't have permission to access this feature. Please contact your store administrator for access.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => window.history.back()}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-transparent text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg font-medium transition-colors"
              >
                Go Back
              </button>
              <button
                onClick={() => window.location.href = '/neo-intelligence'}
                className="w-full px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-colors"
              >
                Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check admin-level permissions
  if (requireAdmin) {
    const { stores, currentShop } = useAuth();
    const currentStore = stores.find((s: any) => s.shop === currentShop);
    const userRole = currentStore?.user_role || 'member';
    
    if (userRole !== 'owner' && userRole !== 'admin') {
      return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
          <div className="max-w-md w-full mx-4 text-center">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
              <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Admin Access Required
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                This feature requires administrator privileges. Your current role is {userRole}.
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => window.history.back()}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-transparent text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg font-medium transition-colors"
                >
                  Go Back
                </button>
                <button
                  onClick={() => window.location.href = '/neo-intelligence'}
                  className="w-full px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-colors"
                >
                  Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }
  }

  // User is authenticated and has permissions
  return <>{children}</>;
}
